import React, { useState, useEffect } from "react";
import { MapPin, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import AddressModal from "@/components/AddressModal";
import useAddress from "@/hooks/useAddress";

const AddressSelectorModal = ({
  isOpen,
  onClose,
  title,
  setAsDefault = true,
  selectedAddressId: externalSelectedId,
  onAddressSelected,
  showAddButton = true,
  addresses: externalAddresses, // Optional: addresses passed from outside
  onAddressComplete: externalOnAddressComplete, // Optional: callback for address creation
}) => {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectingAddressId, setSelectingAddressId] = useState(null);

  const {
    addresses: internalAddresses,
    userDefaultAddress,
    handleSetDefaultAddress,
    handleAddressComplete,
    fetchAddresses,
    isLoading,
  } = useAddress({});

  // Use external addresses if provided, otherwise use internal
  const addresses = externalAddresses || internalAddresses;

  useEffect(() => {
    // Only fetch if addresses are not provided from outside
    if (!externalAddresses && isOpen) {
      fetchAddresses();
    }
  }, [isOpen, externalAddresses]);

  const selectedAddressId = externalSelectedId || userDefaultAddress?._id;

  const handleAddressClick = async (addressId) => {
    if (selectingAddressId) return;

    if (setAsDefault) {
      try {
        setSelectingAddressId(addressId);
        await handleSetDefaultAddress(addressId);
      } finally {
        setSelectingAddressId(null);
      }
    }

    if (onAddressSelected) {
      onAddressSelected(addressId);
    }
    onClose();
  };

  const handleNewAddressComplete = async (addressData) => {
    if (externalOnAddressComplete) {
      // Use external callback if provided
      await externalOnAddressComplete(addressData);
    } else {
      // Use internal handler
      await handleAddressComplete(addressData);
      await fetchAddresses();
    }
    setIsAddModalOpen(false);

    if (onAddressSelected) {
      onAddressSelected(addressData);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title}>
        {isLoading && !addresses ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-100"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {addresses
              ?.slice()
              .sort((a, b) => {
              // Don't sort while loading to prevent list jumping
              if (selectingAddressId) return 0;

              const aIsSelected = selectedAddressId === a._id;
              const bIsSelected = selectedAddressId === b._id;
              if (aIsSelected) return -1;
              if (bIsSelected) return 1;
              return 0;
            })
            .map((address) => {
              const isSelected = selectedAddressId === address._id;
              const isLoadingThis = selectingAddressId === address._id;
              const isDisabled = selectingAddressId && !isLoadingThis;
              const hasDeletedLocation = address.nearby?.isDeleted;

              return (
                <div
                  key={address._id}
                  className={`border rounded-lg p-4 transition-all relative ${
                    hasDeletedLocation
                      ? "cursor-not-allowed opacity-70"
                      : "cursor-pointer"
                  } ${
                    isSelected
                      ? "border-primary-100 bg-primary-100/10 dark:bg-primary-100/20 ring-2 ring-primary-100"
                      : "border-gray-200 dark:border-gray-700 hover:border-primary-100/50"
                  } ${isDisabled || hasDeletedLocation ? "pointer-events-none" : ""}`}
                  onClick={() => handleAddressClick(address._id)}
                >
                  <div className="flex items-start gap-3">
                    <MapPin
                      className={`w-5 h-5 flex-shrink-0 mt-1 ${
                         hasDeletedLocation ? "text-orange-500" : isSelected ? "text-primary-100" : "text-gray-400"
                      }`}
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {address.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {address.address}, {address.city}, {address.state} -{" "}
                        {address.pin_code}
                      </p>
                      {address.landmark && (
                        <p className="text-sm text-gray-500 dark:text-gray-500 mb-1">
                          Landmark: {address.landmark}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Phone: {address.mobile}
                      </p>
                      
                      {/* Warning badge for deleted location - moved to bottom */}
                      {hasDeletedLocation && (
                        <div className="mt-2 bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 text-xs px-2 py-1 rounded-full inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          <span>{t("addresses.locationUnavailable")}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {isLoadingThis ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-100"></div>
                      ) : isSelected ? (
                        <div className="w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center">
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}

          {showAddButton && (
            <div className="sticky bottom-0 section pt-2 border-none">
              <button
                onClick={() => setIsAddModalOpen(true)}
                disabled={!!selectingAddressId}
                className="w-full border-2 border-dashed border-primary-100 text-primary-100 rounded-lg p-4 hover:bg-primary-50/20 dark:hover:bg-primary-100/10 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + {t("cart.deliveryAddress.addNewAddress")}
              </button>
            </div>
          )}
          </div>
        )}
      </Modal>

      {/* Add New Address Modal */}
      <AddressModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        initialValues={{}}
        onComplete={handleNewAddressComplete}
        isEdit={false}
      />
    </>
  );
};

export default AddressSelectorModal;
